/*******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format

/** Namespace for DOM helper functions
 * @ignore
 */

import { isNull, isArr, isStr, isFn, log, toBool } from "./highcharts-editor";

  /** Check if a node is visible
     *  @namespace highed.dom
     *  @param node {domnode} - the node to check
     */
  export function isVisible(node) {
    let style = window.getComputedStyle(node);
    return style.display !== 'none';
  };

  /** Append a set of nodes to another node.
     * Arguments supplied after the @param {} target represents the children to append.
     * @namespace highed.dom
     * @param target {object} - the node to append to
     * @return {domnode} - the target
     */
  export function ap(target) {
    let children = Array.prototype.slice.call(arguments);
    children.splice(0, 1);

    target = getNode(target);

    if (!isNull(target) && typeof target.appendChild !== 'undefined') {
      children.forEach(function(child) {
        if (isArr(child)) {
          child.forEach(function(sc) {
            ap(target, sc);
          });
        } else if (
          typeof child !== 'undefined' &&
          typeof child.appendChild !== 'undefined'
        ) {
          target.appendChild(child);
        } else if (child !== false) {
          log(1, 'child is not valid (ap)');
        }
      });
    } else {
      log(1, 'target is not a valid DOM node (ap)');
    }

    return target;
  };

  /** Create a set of options for a select
     * @namespace highed.dom
     * @param select {HTMLSelect} - the dropdown to add options to
     * @param options {(array|object)} - the options as an array or as an object keyed on ID
     * @param selected {number} - the index of the selected option
     */
  export function options(select, options, selected) {
    if (isNull(options)) {
    } else if (isArr(options)) {
      options.forEach(function(option) {
        ap(select, cr('option', '', option, option));
      });

      if (selected) {
        select.selectedIndex = selected;
      }
    } else if (isStr(options)) {
      try {
        options(select, JSON.parse(options));
      } catch (e) {
        log(e + ' in highed.options (json parser)');
      }
    } else {
      Object.keys(options).forEach(function(key) {
        ap(select, cr('option', '', options[key], key));
      });
    }
  };

  /** Show a node when another is hovered
     * @namespace highed.dom
     * @param parent {object} - the node to listen for the hover on
     * @param child {object} - the node to show when the parent is hovered
     */
  export function showOnHover(parent, child) {
    if (isArr(child)) {
      child.forEach(function(c) {
        showOnHover(parent, c);
      });
      return;
    }

    on(parent, 'mouseover', function() {
      setStyle(child, {
        //display: 'block',
        opacity: 1,
        //  background: 'rgba(46, 46, 46, 0.85)',
        'pointer-events': 'auto'
      });
    });

    on(parent, 'mouseout', function() {
      setStyle(child, {
        //display: 'none',
        opacity: 0,
        //background: 'rgba(0, 0, 0, 0)',
        'pointer-events': 'none'
      });
    });
  };

  /** Create a new SVG HTML node
     * @namespace highed.dom
     * @param type {string} - the type of node to create
     * @param cssClass {string} (optional) - the css class to use for the node
     * @param innerHTML {string} (optional) - the inner html of the new node
     * @param id {string} (optional) - the id of the new node
     *
     * @return {domnode} - the new dom node
     */
   export function crSvg(type, cssClass, id) {
    let res = false;

    if (typeof type !== 'undefined') {
      res = document.createElementNS( "http://www.w3.org/2000/svg", type);

      if (typeof cssClass !== 'undefined') {
        res.setAttribute('class', cssClass);
      }

      if (typeof id !== 'undefined') {
        res.setAttribute('id', id);
      }
    } else {
      log(1, 'no node type supplied (cr');
    }

    return res;
  };

  /** Create a new HTML node
     * @namespace highed.dom
     * @param type {string} - the type of node to create
     * @param cssClass {string} (optional) - the css class to use for the node
     * @param innerHTML {string} (optional) - the inner html of the new node
     * @param id {string} (optional) - the id of the new node
     *
     * @return {domnode} - the new dom node
     */
  export function cr(type, cssClass, innerHTML, id) {
    let res = false;

    if (typeof type !== 'undefined') {
      res = document.createElement(type);

      if (typeof cssClass !== 'undefined') {
        res.className = cssClass;
      }
      if (typeof innerHTML !== 'undefined' && typeof innerHTML !== 'object') {
        res.innerHTML = innerHTML;
      }

      if (typeof id !== 'undefined') {
        res.id = id;
      }
    } else {
      log(1, 'no node type supplied (cr');
    }

    return res;
  };

  /** Style a node
     * @namespace highed.dom
     * @param nodes {(object|array)}  - the node to style. Can also be an array
     * @param style {object} - object containing style properties
     *
     * @return {anything} - whatever was supplied to @param {} nodes
     */
  export function setStyle(nodes, styling) {
    if (isArr(nodes)) {
      nodes.forEach(function(node) {
        setStyle(node, styling);
      });
      return nodes;
    }

    if (nodes && nodes.style) {
      Object.keys(styling).forEach(function(p) {
        nodes.style[p] = styling[p];
      });
      return nodes;
    }
    return false;
  };

  /** Attach an event listener to a dom node
     * @namespace highed.dom
     * @param target {object} - the dom node to attach to
     * @param event {string} - the event to listen for
     * @param callback {function} - the function to call when the event is emitted
     * @param context {object} (optional) - the context of the callback function
     *
     * @return {function} - a function that can be called to unbind the handler
     */
  export function on(target, event, callback, context) {
    let s = [];

    if (!target) {
      return function() {};
    }

    if (isArr(event)) {
      event.forEach(function(event) {
        s.push(on(target, event, callback, context));
      });

      return function() {
        s.forEach(function(f) {
          f();
        });
      };
    }

    if (target === document.body && event === 'resize') {
      //Need some special magic here eventually.
    }

    if (target && target.forEach) {
      target.forEach(function(t) {
        s.push(on(t, event, callback));
      });
    }

    if (s.length > 0) {
      return function() {
        s.forEach(function(f) {
          f();
        });
      };
    }

    function actualCallback() {
      if (isFn(callback)) {
        return callback.apply(context, arguments);
      }
      return;
    }

    if (target.addEventListener) {
      target.addEventListener(event, actualCallback, false);
    } else {
      target.attachEvent('on' + event, actualCallback, false);
    }

    return function() {
      if (window.removeEventListener) {
        target.removeEventListener(event, actualCallback, false);
      } else {
        target.detachEvent('on' + event, actualCallback);
      }
    };
  };

  export function nodefault(e) {
    e.cancelBubble = true;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  };

  /** Get or set the value of a node
     * @namespace highed.dom
     * @param node {object} - the node to get the value of
     * @param value {(string|bool|number)} (optional) - the value to set
     * @return {anything} - the value
     */
  export function val(node, value) {
    if (node.tagName === 'SELECT') {
      if (node.selectedIndex >= 0) {
        if (!isNull(value)) {
          for (let i = 0; i < node.options.length; i++) {
            if (node.options[i].id === value) {
              node.selectedIndex = i;
              break;
            }
          }
        }
        return node.options[node.selectedIndex].id;
      }
    } else if (node.tagName === 'INPUT') {
      if (node.type === 'checkbox') {
        if (!isNull(value)) {
          node.checked = toBool(value);
        }
        return node.checked;
      }
      if (!isNull(value)) {
        node.value = value;
      }
      return node.value;
    } else {
      if (!isNull(value)) {
        node.innerHTML = value;
      }
      return node.innerText;
    }

    return false;
  };

  /** Get the size of a node
     * @namespace highed.dom
     * @param node {object} - the node to get the size of
     * @return {object} - the size as an object `{w, h}`
     */
  export function getElementSize(node) {
    return {
      w: node.clientWidth,
      h: node.clientHeight
    };
  };

  /** Get the position of a node
     * @namespace highed.dom
     * @param node {object} - the node to get the position of
     * @param abs {boolean} - absolute calculation rather than parent relative
     * @return {object} - the position as an object `{x, y}`
     */
  export function getElementPosition(node, abs) {
    let x = 0,
      y = 0;

    if (abs) {
      let b = node.getBoundingClientRect();
      
      return {
        x: b.left + (window.scrollX || 0),
        y: b.top + (window.scrollY || 0)
      };

    }

    return {
      x: node.offsetLeft,
      y: node.offsetTop
    };
  };

  /** Find a node
     * @namespace highed.dom
     * @param node {object} - the node to find. Either a string or an actual node instance
     * @return {object} - the node or false if the node was not found
     */
  export function getNode(node) {
    if (node && node.appendChild) {
      return node;
    }
    return document.getElementById(node) || false;
  };

  /** Create a button
     * @namespace highed.dom
     * @param type {string} - the name of the button
     * @param cssClass {string} (optional) - the css class to use for the button
     * @param id {string} (optional) - the id of the new button
     * @param func {string} (optional) - the function to call when button clicked
     *
     * @return {domnode} - the new button
     */
  export function crBtn(name, className, id, func) {
     let btn = cr('button', className, name, id);

     on(btn, 'click', function(e){
      if (func && isFn(func)) func();
     });

     return btn;
   };